import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle, Shield, MessageSquare, TrendingDown, Zap, AlertCircle, Info, Quote } from 'lucide-react';
interface FindingCardProps {
  pattern: string;
  severity: string;
  example?: string;
  index?: number;
}
export function FindingCard({
  pattern,
  severity,
  example,
  index = 0
}: FindingCardProps) {
  // Helper for icons based on pattern name
  const getIcon = (p: string) => {
    const lower = p.toLowerCase();
    if (lower.includes('gaslight')) return AlertTriangle;
    if (lower.includes('manipulat')) return Shield;
    if (lower.includes('dismiss')) return MessageSquare;
    if (lower.includes('blame')) return TrendingDown;
    if (lower.includes('abuse')) return AlertCircle;
    if (lower.includes('aggress')) return Zap;
    return Info;
  };
  const Icon = getIcon(pattern);
  // Severity-based styling
  const getSeverityConfig = (s: string) => {
    const normalized = s.toLowerCase();
    if (normalized === 'high') {
      return {
        accentGradient: 'from-red-500 to-red-600',
        badgeBg: 'bg-red-50',
        badgeText: 'text-red-700',
        badgeBorder: 'border-red-200',
        badgeShadow: 'shadow-[0_2px_8px_rgba(239,68,68,0.15)]',
        iconBg: 'bg-gradient-to-br from-red-100 to-red-50',
        iconText: 'text-red-600',
        iconBorder: 'border-red-100',
        quoteBorder: 'border-red-400',
        quoteBg: 'bg-gradient-to-r from-red-50/80 to-transparent',
        accentColor: '#EF4444'
      };
    }
    if (normalized === 'medium') {
      return {
        accentGradient: 'from-orange-400 to-orange-500',
        badgeBg: 'bg-orange-50',
        badgeText: 'text-orange-700',
        badgeBorder: 'border-orange-200',
        badgeShadow: 'shadow-[0_2px_8px_rgba(249,115,22,0.15)]',
        iconBg: 'bg-gradient-to-br from-orange-100 to-orange-50',
        iconText: 'text-orange-600',
        iconBorder: 'border-orange-100',
        quoteBorder: 'border-orange-400',
        quoteBg: 'bg-gradient-to-r from-orange-50/80 to-transparent',
        accentColor: '#F59E0B'
      };
    }
    return {
      accentGradient: 'from-emerald-400 to-emerald-500',
      badgeBg: 'bg-emerald-50',
      badgeText: 'text-emerald-700',
      badgeBorder: 'border-emerald-200',
      badgeShadow: 'shadow-[0_2px_8px_rgba(16,185,129,0.15)]',
      iconBg: 'bg-gradient-to-br from-emerald-100 to-emerald-50',
      iconText: 'text-emerald-600',
      iconBorder: 'border-emerald-100',
      quoteBorder: 'border-emerald-400',
      quoteBg: 'bg-gradient-to-r from-emerald-50/80 to-transparent',
      accentColor: '#10B981'
    };
  };
  const config = getSeverityConfig(severity);
  return <motion.div initial={{
    opacity: 0,
    y: 24
  }} animate={{
    opacity: 1,
    y: 0
  }} whileHover={{
    y: -2,
    boxShadow: '0 12px 24px -4px rgba(0,0,0,0.06), 0 4px 12px -2px rgba(0,0,0,0.04)'
  }} transition={{
    delay: index * 0.08,
    type: 'spring',
    stiffness: 280,
    damping: 22
  }} className="bg-[#FAFAFA] rounded-2xl border border-slate-200/80 overflow-hidden h-full flex flex-col relative transition-shadow duration-300" style={{
    boxShadow: '0 2px 8px -2px rgba(0,0,0,0.04), 0 1px 4px -1px rgba(0,0,0,0.02)'
  }} data-id="element-85">
      {/* Top Accent Line */}
      <div className={`h-[3px] w-full bg-gradient-to-r ${config.accentGradient}`} data-id="element-86" />

      {/* Left Accent Line */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5" style={{
      backgroundColor: config.accentColor
    }} data-id="element-87" />

      <div className="p-6 md:p-8 flex flex-col flex-1 pl-8 md:pl-10" data-id="element-88">
        {/* Header Row */}
        <div className="flex items-start gap-4 md:gap-5 mb-5 md:mb-6" data-id="element-89">
          {/* Icon Container */}
          <div className={`w-10 h-10 md:w-12 md:h-12 rounded-xl flex items-center justify-center flex-shrink-0 border ${config.iconBg} ${config.iconBorder} shadow-inner`} data-id="element-90">
            <Icon className={`w-5 h-5 md:w-6 md:h-6 ${config.iconText}`} strokeWidth={2} data-id="element-91" />
          </div>

          {/* Title and Badge */}
          <div className="flex-1 min-w-0 pt-1" data-id="element-92">
            <div className="flex items-center justify-between gap-4 flex-wrap" data-id="element-93">
              <h3 className="text-lg md:text-xl font-black text-slate-900 tracking-tight capitalize leading-tight" data-id="element-94">
                {pattern}
              </h3>
              <span className={`px-3 py-1.5 rounded-full text-[10px] font-mono font-bold uppercase tracking-[0.15em] border ${config.badgeBg} ${config.badgeText} ${config.badgeBorder} ${config.badgeShadow}`} data-id="element-95">
                {severity}
              </span>
            </div>
          </div>
        </div>

        {/* Quote/Example Section */}
        <div className="mt-auto" data-id="element-96">
          {example ? <div className="relative" data-id="element-97">
              {/* Decorative Quote Icon */}
              <div className="absolute -top-4 -left-2 opacity-[0.06]" data-id="element-98">
                <Quote size={36} fill="currentColor" className="text-slate-900" data-id="element-99" />
              </div>

              {/* Quote Block */}
              <blockquote className={`relative z-10 text-[15px] text-slate-700 font-medium italic leading-relaxed p-5 rounded-r-xl border-l-[4px] ${config.quoteBorder} ${config.quoteBg}`} data-id="element-100">
                "{example}"
              </blockquote>
            </div> : <div className="bg-slate-50/80 rounded-xl p-5 border border-slate-100 flex items-center gap-3" data-id="element-101">
              <Info size={20} className="text-slate-400 flex-shrink-0" data-id="element-102" />
              <span className="text-sm font-medium text-slate-500" data-id="element-103">
                This pattern was detected multiple times in the conversation.
              </span>
            </div>}
        </div>
      </div>
    </motion.div>;
}